// IE 2026 11 01 / ODOO-350449 (заказ Игоря: ієрархічна шапка і гілки дерева у теплокарті, як у таблиці)
// Чистая логика дерева вкладки «Heatmap» (AttrHeatmap.vue) поверх матрицы heatmap_build (utils/heatmapMatrix.js):
// — шапка X иерархией по шагу: day — рік → місяць → день; week — рік → місяць → тиждень (месяц — по понедельнику недели); month — рік → місяць;
//   quarter — рік → квартал; year — рік (уровней над листом нет). Узел уровня выше листа — полоса с глифом свёртки (как группа колонок REPORT2);
//   свёрнутый узел — ОДНА колонка с агрегатом листьев (агрегат меры heatmap_measure_agg: sum — сумма, max — максимум, = ячейка шага узла), категории — суммой;
// — группы строк (hm_y1) свёртываются как дерево строк сетки; свёрнутая группа — одна строка с агрегатом строк группы по колонкам.
// Состояние свёртки — массивы id узлов в компоненте (не в записи); Shift+клик — свернуть / развернуть все узлы уровня.
// Выключатель fe.ux.heatmapTreeHeader (дефолт ВКЛ, §8): ВЫКЛ — прежняя полоса месяца / года и прежние группы. Юнит — tests/unit/heatmapTree.test.mjs
import { heatmap_cell_keys, heatmap_cell_cats, heatmap_levels, HEATMAP_X_GROUP_SEP } from '@/utils/heatmapMatrix'   // IE 2026 11 03 / ODOO-350610 (heatmap category axis): + HEATMAP_X_GROUP_SEP

export const HEATMAP_TREE_FLAG = 'fe.ux.heatmapTreeHeader'
export function heatmapTree_on(setting) {   // чтение флага (нет читателя / ошибка — ВКЛ)
    try { return typeof setting === 'function' ? setting(HEATMAP_TREE_FLAG, true) !== false : true } catch (e) { return true }
}

const KINDS = { day: ['year', 'month'], week: ['year', 'month'], month: ['year'], quarter: ['year'], year: [] }
const PFX = { year: 'y', month: 'm', xgroup: 'g' }   // IE 2026 11 03 / ODOO-350610 (heatmap category axis): xgroup — группа колонок категорий

// уровни над листом для шага (сверху вниз)
export function heatmapTree_kinds(step) { return (KINDS[String(step || '').toLowerCase()] || []).slice() }
// уровни над листом по шагу и столбцам: ось категориями (step 'category') с группами колонок (hm_x_group, ключ '<группа>\u0001<категория>') — уровень xgroup, без групп — нет   // IE 2026 11 03 / ODOO-350610 (heatmap category axis)
export function heatmapTree_kindsOf(step, cols) {   // IE 2026 11 03 / ODOO-350610 (heatmap category axis)
    if (String(step || '').toLowerCase() !== 'category') return heatmapTree_kinds(step)   // IE 2026 11 03 / ODOO-350610 (heatmap category axis)
    return (Array.isArray(cols) ? cols : []).some(c => c && String(c.key == null ? '' : c.key).includes(HEATMAP_X_GROUP_SEP)) ? ['xgroup'] : []   // IE 2026 11 03 / ODOO-350610 (heatmap category axis)
}   // IE 2026 11 03 / ODOO-350610 (heatmap category axis)

// ключ узла уровня kind для ключа столбца: year — 'YYYY', month — 'YYYY-MM' (день / понедельник недели); id узла — 'y:2026', 'm:2026-02'
export function heatmapTree_nodeKey(colKey, kind) {
    const k = String(colKey == null ? '' : colKey)
    if (kind === 'xgroup') return k.split(HEATMAP_X_GROUP_SEP)[0]   // IE 2026 11 03 / ODOO-350610 (heatmap category axis)
    return kind === 'year' ? k.slice(0, 4) : k.slice(0, 7)
}
export const heatmapTree_nodeId = (kind, key) => `${PFX[kind] || kind}:${key}`

// id всех узлов уровня kind по столбцам матрицы (в порядке столбцов, без повторов)
export function heatmapTree_levelIds(cols, step, kind) {
    if (!heatmapTree_kindsOf(step, cols).includes(kind)) return []   // IE 2026 11 03 / ODOO-350610 (heatmap category axis): было heatmapTree_kinds(step)
    const out = []
    for (const c of Array.isArray(cols) ? cols : []) {
        const id = heatmapTree_nodeId(kind, heatmapTree_nodeKey(c && c.key, kind))
        if (out[out.length - 1] !== id && !out.includes(id)) out.push(id)
    }
    return out
}

// клик по глифу узла id (kind — его уровень): без Shift — переключить узел; Shift — все узлы уровня: узел свёрнут — развернуть все, иначе — свернуть все.
// collapsed — массив id; результат — новый массив (порядок вставки)
export function heatmapTree_toggle(collapsed, id, opts = {}) {
    const cur = new Set(Array.isArray(collapsed) ? collapsed : [])
    const was = cur.has(id)
    const ids = opts.shift && Array.isArray(opts.levelIds) && opts.levelIds.length ? opts.levelIds : [id]
    for (const x of ids) { if (was) cur.delete(x); else cur.add(x) }
    return [...cur]
}

// столбцы матрицы + свёрнутые узлы → дерево шапки:
// { kinds, cols — показанные столбцы (лист — исходный объект столбца; свёрнутый узел — { key, label, group, split, node, kind, x_from, x_to, leaves }),
//   map — индексы листьев на каждый показанный столбец, levels — по уровню массив полос { id, kind, key, level, start, span, collapsed, filler, leaves } }
// filler — пустая полоса под свёрнутым узлом верхнего уровня (сетка шапки сплошная)
export function heatmapTree_cols(cols, step, collapsed) {
    const C = Array.isArray(cols) ? cols : []
    const kinds = heatmapTree_kindsOf(step, C)   // IE 2026 11 03 / ODOO-350610 (heatmap category axis): было heatmapTree_kinds(step)
    const coll = collapsed instanceof Set ? collapsed : new Set(Array.isArray(collapsed) ? collapsed : [])
    const view = []
    C.forEach((c, i) => {
        const anc = kinds.map((kind, level) => { const key = heatmapTree_nodeKey(c && c.key, kind); return { kind, key, level, id: heatmapTree_nodeId(kind, key) } })
        const top = anc.findIndex(n => coll.has(n.id))
        if (top < 0) { view.push({ node: null, anc, leaves: [i] }); return }
        const last = view[view.length - 1]
        if (last && last.node && last.node.id === anc[top].id) { last.leaves.push(i); return }
        view.push({ node: anc[top], anc: anc.slice(0, top), leaves: [i] })
    })
    const outCols = view.map(v => {
        if (!v.node) return C[v.leaves[0]]
        const first = C[v.leaves[0]], last = C[v.leaves[v.leaves.length - 1]]
        const kf = heatmap_cell_keys(first, null, null, {}, step), kl = heatmap_cell_keys(last, null, null, {}, step)
        return { key: v.node.key, label: v.node.key, group: '', split: v.leaves.some(i => C[i] && C[i].split), node: v.node.id, kind: v.node.kind,
            x_from: kf.x_from, x_to: kl.x_to, leaves: v.leaves.length }
    })
    const levels = kinds.map((kind, level) => {
        const out = []
        view.forEach((v, vi) => {
            let band
            if (v.anc.length > level) band = { ...v.anc[level], collapsed: false, filler: false }
            else if (v.node && v.node.level === level) band = { ...v.node, collapsed: true, filler: false }
            else band = { id: 'f:' + level + ':' + vi, kind, key: '', level, collapsed: false, filler: true }
            const prev = out[out.length - 1]
            if (prev && !band.filler && prev.id === band.id) { prev.span++; prev.leaves += v.leaves.length; return }
            out.push({ ...band, start: vi, span: 1, leaves: v.leaves.length })
        })
        return out
    })
    return { kinds, cols: outCols, map: view.map(v => v.leaves), levels }
}

// IE 2026 11 01 / ODOO-350449 (заказ Игоря: ієрархічна шапка і гілки дерева у теплокарті, як у таблиці; проба dev02 p160: distinct-міри у згорнутому вузлі)
// ячейка «данные узла ещё идут с бэка» (колонка показывает «…» без пузыря; в max / уровни не входит)
export const HEATMAP_TREE_PENDING = Object.freeze({ pending: true, value: 0, n: 0, cat: null, idx: [] })
const isPending = (c) => !!(c && c.pending)

// ячейки → одна ячейка агрегата: value — сумма или максимум (agg 'max'), n — сумма, idx — подряд; категории — суммой (доли и доминирующая — заново);
// нет ни одной — null; одна — она сама
export function heatmapTree_mergeCells(cells, agg) {
    const L = (Array.isArray(cells) ? cells : []).filter(Boolean)
    if (!L.length) return null
    if (L.some(isPending)) return HEATMAP_TREE_PENDING   // IE 2026 11 01 / ODOO-350449 (заказ Игоря: ієрархічна шапка і гілки дерева у теплокарті, як у таблиці; проба dev02 p160: distinct-міри у згорнутому вузлі)
    if (L.length === 1) return L[0]
    const isMax = agg === 'max'
    const out = { value: isMax ? L[0].value : 0, n: 0, cat: null, idx: [] }
    let cats = null
    for (const c of L) {
        out.value = isMax ? Math.max(out.value, Number(c.value) || 0) : out.value + (Number(c.value) || 0)
        out.n += Number(c.n) || 0
        if (Array.isArray(c.idx)) out.idx.push(...c.idx)
        if (Array.isArray(c.cats)) {
            cats = cats || new Map()
            for (const x of c.cats) { const a = cats.get(x.key) || { sum: 0, n: 0 }; a.sum += Number(x.value) || 0; a.n += Number(x.n) || 0; cats.set(x.key, a) }
        }
    }
    const rr = L[0].rows   // IE 2026 11 01 / ODOO-350449 (проба dev02 p160: distinct-міри у згорнутому вузлі): ячейки из строк шага узла — idx адресует их
    if (rr && L.every(c => c.rows === rr)) out.rows = rr
    if (cats) { out.cats = heatmap_cell_cats(cats); out.cat = out.cats.length ? out.cats[0].key : null }
    else out.cat = (L.find(c => c.cat !== null && c.cat !== undefined) || {}).cat ?? null
    return out
}

const rowMax = (cells) => cells.reduce((a, c) => (!c || isPending(c) ? a : (a === null || c.value > a ? c.value : a)), null)   // IE 2026 11 01 / ODOO-350449 (заказ Игоря: ієрархічна шапка і гілки дерева у теплокарті, як у таблиці; проба dev02 p160: distinct-міри у згорнутому вузлі): «…» — не значение

// матрица + дерево шапки (heatmapTree_cols) + свёрнутые группы → матрица показа: столбцы — показанные, ячейки строк — по листьям столбца (агрегат),
// у группы — collapsed и agg (строка агрегата строк группы по показанным столбцам, title — opts.totalTitle); max строк / групп, max и levels матрицы — по показанным ячейкам
// (свёрнутая группа — её строка агрегата вместо строк). opts.agg — агрегат меры ('sum' | 'max'), opts.rowsCollapsed — массив ключей групп;
// opts.exact — { <kind узла>: heatmapTree_exact(...) } — значения свёрнутых узлов с бэка (список с окном DS: distinct-меры не складываются по дням), нет — агрегат листьев
export function heatmapTree_apply(matrix, tree, opts = {}) {
    const M = matrix && typeof matrix === 'object' ? matrix : { cols: [], groups: [] }
    const T = tree && Array.isArray(tree.map) ? tree : { cols: M.cols, map: (M.cols || []).map((c, i) => [i]) }
    const agg = opts.agg === 'max' ? 'max' : 'sum'
    const rc = new Set(Array.isArray(opts.rowsCollapsed) ? opts.rowsCollapsed : [])
    const same = T.map.length === (M.cols || []).length && T.map.every((l, i) => l.length === 1 && l[0] === i)
    const values = []
    let max = 0
    const seen = (cells) => { for (const c of cells) if (c && !isPending(c)) { values.push(c.value); if (c.value > max) max = c.value } }
    const groups = (M.groups || []).map(g => {
        const rows = (g.rows || []).map(r => {
            const cells = same ? r.cells : T.map.map((l, vi) => { const x = heatmapTree_nodeCell(T.cols[vi], g.key, r.key, opts.exact); return x !== undefined ? x : heatmapTree_mergeCells(l.map(i => r.cells[i]), agg) })   // IE 2026 11 01 / ODOO-350449 (заказ Игоря: ієрархічна шапка і гілки дерева у теплокарті, як у таблиці; проба dev02 p160: distinct-міри у згорнутому вузлі): свёрнутый узел — точная ячейка шага узла с бэка
            return same ? r : { ...r, cells, max: rowMax(cells) }
        })
        const collapsed = rc.has(g.key)
        const out = { ...g, rows, collapsed, max: rows.reduce((a, r) => (r.max === null ? a : (a === null ? r.max : Math.max(a, r.max))), null) }
        if (collapsed) {
            const cells = T.cols.map((c, ci) => heatmapTree_mergeCells(rows.map(r => r.cells[ci]), agg))
            out.agg = { key: '', title: opts.totalTitle || 'Total', cells, first: g.first, extra: g.extra, total: g.total, sum: g.sum, max: rowMax(cells), colTotal: g.colTotal, isGroup: true }
            seen(cells)
        } else rows.forEach(r => seen(r.cells))
        return out
    })
    return { ...M, cols: T.cols, groups, max, levels: heatmap_levels(values) }
}

// итог узла по всем строкам (агрегат меры по листьям узла): { value, n } | null
export function heatmapTree_nodeTotal(matrix, colIdx, agg) {
    const set = new Set(Array.isArray(colIdx) ? colIdx : [])
    const cells = []
    for (const g of (matrix && matrix.groups) || []) for (const r of g.rows || []) (r.cells || []).forEach((c, i) => { if (c && set.has(i)) cells.push(c) })
    const m = heatmapTree_mergeCells(cells, agg)
    return m ? { value: m.value, n: m.n } : null
}

// индексы листьев (столбцов матрицы) узла id
export function heatmapTree_nodeLeaves(cols, id) {
    const s = String(id || ''), i = s.indexOf(':')
    const kind = s.slice(0, i) === 'y' ? 'year' : (s.slice(0, i) === 'g' ? 'xgroup' : 'month'), key = s.slice(i + 1)   // IE 2026 11 03 / ODOO-350610 (heatmap category axis): 'g:' — группа колонок категорий
    const out = []
    ;(Array.isArray(cols) ? cols : []).forEach((c, ci) => { if (heatmapTree_nodeKey(c && c.key, kind) === key) out.push(ci) })
    return out
}

// ==== IE 2026 11 01 / ODOO-350449 (заказ Игоря: ієрархічна шапка і гілки дерева у теплокарті, як у таблиці; проба dev02 p160: distinct-міри у згорнутому вузлі) ====
// Список с окном DS (hm_period): бэк считает строки по шагу запроса (params.heatmap.step), и distinct-меры («Changed records», «Users») у месяца ≠ сумме дней.
// Значение свёрнутого узла берётся из строк шага узла (year → 'year', month → 'month'), подгруженных отдельным select-ом в кэш вкладки (stepRows); пока строк нет — «…».
// Списки без окна DS (шаг перегруппировывает сырые строки на фронте) — прежний агрегат листьев (heatmap_measure_agg)
export const HEATMAP_TREE_STEP_OF = { year: 'year', month: 'month', quarter: 'quarter' }

// шаги, строки которых нужны свёрнутым колонкам показа (по порядку, без повторов)
export function heatmapTree_needSteps(cols) {
    const out = []
    for (const c of Array.isArray(cols) ? cols : []) {
        const st = c && c.node ? HEATMAP_TREE_STEP_OF[c.kind] : null
        if (st && !out.includes(st)) out.push(st)
    }
    return out
}

// ключ кэша строк шага: шаг + окно + дата начала + мера (то, с чем бэк считает строки)
export function heatmapTree_stepKey(step, hm) {
    const H = hm && typeof hm === 'object' ? hm : {}
    return [step, H.days ?? '', H.date_from ?? '', H.measure ?? ''].map(x => String(x)).join('\u0001')
}

// запись кэша → источник точных ячеек для heatmapTree_apply: entry { state: 'loading' | 'ok' | 'error', matrix? (heatmap_build по строкам шага), rows? }
// ok — индекс 'группа␁строка␁ключ столбца' → ячейка (rows — строки шага: idx ячейки адресует их); loading — «…»; error / нет — null (агрегат листьев)
export function heatmapTree_exact(entry) {
    if (!entry || typeof entry !== 'object') return null
    if (entry.state === 'loading') return { state: 'loading' }
    if (entry.state !== 'ok' || !entry.matrix) return null
    const index = new Map()
    for (const g of entry.matrix.groups || []) for (const r of g.rows || []) (r.cells || []).forEach((c, ci) => {
        const col = (entry.matrix.cols || [])[ci]
        if (c && col) index.set(g.key + '\u0001' + r.key + '\u0001' + col.key, c)
    })
    return { state: 'ok', index, rows: Array.isArray(entry.rows) ? entry.rows : null }
}

// ячейка свёрнутой колонки col для строки (группа gk, строка rk) из exact[col.kind]: loading — HEATMAP_TREE_PENDING; ok — ячейка шага (нет — null: пусто);
// не узел / нет источника / ошибка — undefined (вызывающий агрегирует листья)
export function heatmapTree_nodeCell(col, gk, rk, exact) {
    if (!col || !col.node || !exact) return undefined
    const ex = exact[col.kind]
    if (!ex) return undefined
    if (ex.state === 'loading') return HEATMAP_TREE_PENDING
    if (ex.state !== 'ok') return undefined
    const c = ex.index.get(gk + '\u0001' + rk + '\u0001' + col.key)
    if (!c) return null
    return ex.rows ? { ...c, rows: ex.rows } : c
}

// ширины столбцов показа: лист — colW; свёрнутый узел — не уже текста узла (textW(col) px) + глиф и отступы (pad, по умолчанию 26 px), не уже colW
export function heatmapTree_colWidths(cols, colW, textW, pad = 26) {
    const w = Math.max(1, Number(colW) || 0)
    return (Array.isArray(cols) ? cols : []).map(c => {
        if (!c || !c.node) return w
        let t = 0
        try { t = typeof textW === 'function' ? Number(textW(c)) || 0 : 0 } catch (e) { t = 0 }
        return Math.max(w, Math.ceil(t + pad))
    })
}

// строки списка из ответа select (привязка вкладки, напр. '_doc.data'): answer.record, иначе answer.WIDGET.record, иначе записи actions_after; нет массива — null
export function heatmapTree_answerRows(answer, binding) {
    const A = answer && typeof answer === 'object' ? answer : null
    if (!A) return null
    const path = String(binding || '_doc.data').split('.').filter(Boolean)
    const at = (rec) => path.reduce((o, k) => (o && typeof o === 'object' ? o[k] : undefined), rec)
    const recs = [A.record, A.WIDGET && A.WIDGET.record, ...(Array.isArray(A.actions_after) ? A.actions_after.map(x => x && (x.record || (x.WIDGET && x.WIDGET.record))) : [])]
    for (const r of recs) { const v = r ? at(r) : undefined; if (Array.isArray(v)) return v }
    return null
}
